import { useTranslation } from 'react-i18next';
import { X, Pen } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useRoleForm } from '../hooks/useRoleForm';
import { PermissionsGrid } from './PermissionsGrid';

interface CreateRoleDialogProps {
  id?: number | string;
}

export function CreateRoleDialog({ id }: CreateRoleDialogProps) {
  const { t } = useTranslation();
  const {
    form,
    open,
    setOpen,
    isEdit,
    isPending,
    searchQuery,
    setSearchQuery,
    selectedPermissions,
    isLoadingPermissions,
    groupedPermissions,
    onSubmit,
    handleSaveAndCreate,
    togglePermission,
    selectAll,
    deselectAll,
    selectFiltered,
    deselectFiltered,
  } = useRoleForm(id);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {isEdit ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen className="h-4 w-4" />
          </Button>
        ) : (
          <Button>
            {t('new')} {t('role')}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent className="p-0 sm:max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/30">
          <div>
            <AlertDialogTitle className="text-lg font-semibold">
              {isEdit ? t('update') : t('new')} {t('role')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground mt-0.5">
              {isEdit ? t('editRoleDescription') : t('createRoleDescription')}
            </AlertDialogDescription>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close dialog"
            className="rounded-full p-1.5 hover:bg-muted transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col"
            noValidate
          >
            {/* Form Content */}
            <div className="px-6 py-5 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Basic Info Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  {t('basicInfo')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Name */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('name')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('enterRoleName')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Name EN */}
                  <FormField
                    control={form.control}
                    name="name_en"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('nameEn')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('enterRoleNameEn')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Name RU */}
                  <FormField
                    control={form.control}
                    name="name_ru"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('nameRu')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('enterRoleNameRu')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Status */}
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('status.status')}</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('selectStatus')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ACTIVE">{t('active')}</SelectItem>
                              <SelectItem value="ARCHIVED">{t('archived')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Type */}
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('type')}</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('selectType')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="MANAGER">{t('manager')}</SelectItem>
                              <SelectItem value="STAFF">{t('staff')}</SelectItem>
                              <SelectItem value="PARTNER">{t('partner')}</SelectItem>
                              <SelectItem value="LEAD">{t('lead')}</SelectItem>
                              <SelectItem value="MD">{t('md')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Rate */}
                  <FormField
                    control={form.control}
                    name="rate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('rate')}</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0"
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                </div>
              </div>

              {/* Permissions Section */}
              <PermissionsGrid
                form={form}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedPermissions={selectedPermissions}
                isLoadingPermissions={isLoadingPermissions}
                groupedPermissions={groupedPermissions}
                togglePermission={togglePermission}
                selectAll={selectAll}
                deselectAll={deselectAll}
                selectFiltered={selectFiltered}
                deselectFiltered={deselectFiltered}
              />
            </div>

            {/* Footer */}
            <AlertDialogFooter className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/30">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>

              {isEdit ? (
                <Button
                  type="button"
                  disabled={isPending}
                  onClick={form.handleSubmit(onSubmit)}
                >
                  {isPending ? t('saving') : t('save')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    disabled={isPending}
                    onClick={form.handleSubmit(handleSaveAndCreate)}
                    variant="outline"
                  >
                    {isPending ? t('saving') : t('saveAndCreate')}
                  </Button>
                  <Button
                    type="button"
                    disabled={isPending}
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {isPending ? t('saving') : t('save')}
                  </Button>
                </>
              )}
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
